// A picture an extraction found, imported for its form (#249, spec G15-extract-from-url): the
// server fetches it in the worker and keeps it only if it is the file the extraction read; the
// asset is read until it is ready, as a save from a generation is (`saving-poll.ts`). The
// library shows it only once a brand or product save names it; else the sweep purges it.
import { getAsset, importFromUrl } from "../../api/generated/endpoints/assets/assets";
import type { Asset, ExtractedImage } from "../../api/generated/model";
import { SAVING_MAX_READS, SAVING_POLL_MS } from "./saving-poll";

/** The import did not end ready: refused, gone, or still copying after a minute. */
export class ImportFailedError extends Error {}

function pause(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    });
  });
}

/** The picture as a ready asset of the brand's library, with its signed URLs. */
export async function importPicture(
  teamId: string,
  brandId: string,
  image: ExtractedImage,
  signal?: AbortSignal,
): Promise<Asset> {
  const body = { url: image.url, mime: image.mime, bytes: image.bytes };
  const options = signal === undefined ? {} : { signal };
  const started = await importFromUrl(
    teamId,
    brandId,
    body,
    { "Idempotency-Key": crypto.randomUUID() },
    options,
  );
  let asset = started;
  for (let read = 0; asset.status === "pending" && read < SAVING_MAX_READS; read += 1) {
    await pause(SAVING_POLL_MS, signal);
    asset = await getAsset(teamId, brandId, started.id, options);
  }
  if (asset.status !== "ready") throw new ImportFailedError(`import ${asset.status}`);
  return asset;
}
